import { useState } from "react";
import { api } from "../api";
import type { Contact } from "../types";

interface Props {
  caseId: string;
  contacts: Contact[];
  onContactsChanged: (next: Contact[]) => void;
}

type Draft = {
  id?: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  notes: string;
};

const EMPTY_DRAFT: Draft = {
  name: "",
  role: "",
  email: "",
  phone: "",
  notes: "",
};

function toDraft(c: Contact): Draft {
  return {
    id: c.id,
    name: c.name,
    role: c.role ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    notes: c.notes ?? "",
  };
}

export default function ContactsTab({
  caseId,
  contacts,
  onContactsChanged,
}: Props) {
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const { contacts } = await api.listContacts(caseId);
    onContactsChanged(contacts);
  }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: editing.name.trim(),
        role: editing.role.trim() || null,
        email: editing.email.trim() || null,
        phone: editing.phone.trim() || null,
        notes: editing.notes.trim() || null,
      };
      if (editing.id) await api.updateContact(editing.id, body);
      else await api.createContact(caseId, body);
      await refresh();
      setEditing(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await api.deleteContact(id);
      await refresh();
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <div className="contacts-tab">
      <div className="contacts-header">
        <button
          type="button"
          className="btn-primary"
          onClick={() => setEditing({ ...EMPTY_DRAFT })}
          disabled={editing !== null}
        >
          + Add contact
        </button>
      </div>

      {editing && (
        <div className="contact-form">
          <h3>{editing.id ? "Edit contact" : "New contact"}</h3>
          <div className="row-2">
            <label>
              Name
              <input
                type="text"
                value={editing.name}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
                autoFocus
                required
              />
            </label>
            <label>
              Role
              <input
                type="text"
                value={editing.role}
                onChange={(e) =>
                  setEditing({ ...editing, role: e.target.value })
                }
                placeholder="e.g. Sr. Manager Operations"
              />
            </label>
          </div>
          <div className="row-2">
            <label>
              Email
              <input
                type="email"
                value={editing.email}
                onChange={(e) =>
                  setEditing({ ...editing, email: e.target.value })
                }
              />
            </label>
            <label>
              Phone
              <input
                type="tel"
                value={editing.phone}
                onChange={(e) =>
                  setEditing({ ...editing, phone: e.target.value })
                }
              />
            </label>
          </div>
          <label>
            Notes
            <textarea
              value={editing.notes}
              onChange={(e) =>
                setEditing({ ...editing, notes: e.target.value })
              }
              rows={3}
            />
          </label>
          {error && <div className="form-error">{error}</div>}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {contacts.length === 0 && !editing ? (
        <div className="empty-state">
          <p>No contacts yet. Click + Add contact to record who's involved.</p>
        </div>
      ) : (
        <ul className="contacts-list">
          {contacts.map((c) => (
            <li key={c.id} className="contact-item">
              <div className="contact-main">
                <div className="contact-name">{c.name}</div>
                {c.role && <div className="contact-role">{c.role}</div>}
                <div className="contact-lines">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="contact-email">
                      {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a
                      href={`tel:${c.phone.replace(/\s+/g, "")}`}
                      className="contact-phone"
                    >
                      {c.phone}
                    </a>
                  )}
                </div>
                {c.notes && <div className="contact-notes">{c.notes}</div>}
              </div>
              <div className="contact-actions">
                <button
                  type="button"
                  onClick={() => setEditing(toDraft(c))}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-danger-subtle"
                  onClick={() => remove(c.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
